import { axiosInstance } from "@/lib/axios";
import type {
  CreateGalleryPayload,
  UpdateGalleryPayload,
  GalleryItemsPayload,
  CreateGalleryResponse,
  GalleriesApiResponse,
  GalleriesApiItem,
  GalleriesQueryParams,
} from "./types";

/**
 * The gallery WRITE routes answer failures with translation keys
 * (`messages.gallery.models.not_owner`), not sentences. Left to the global
 * interceptor that key is what the user reads — a dotted identifier, in
 * English, in both locales. Suppressing it hands the message to the call
 * site, which runs the key through `item-errors.ts` and shows real copy.
 *
 * The interceptor's 401 branch runs BEFORE this check, so an expired session
 * still logs out and opens the login modal.
 */
const GALLERY_KEY_ERRORS = { suppressToast: true } as Record<string, unknown>;

export async function getGalleries(
  params: GalleriesQueryParams = {},
): Promise<GalleriesApiResponse> {
  const endpoint = params.watchlist ? "/galleries/watchlist" : "/galleries";

  const p = new URLSearchParams();
  if (params.limit != null) p.set("limit", String(params.limit));
  if (params.model_type) p.set("model_type", params.model_type);
  if (params.name) p.set("name", params.name);
  if (params.slug) p.set("slug", params.slug);
  if (params.user_id != null) p.set("user_id", String(params.user_id));

  const url = `${endpoint}${p.toString() ? `?${p.toString()}` : ""}`;
  const { data } = await axiosInstance.get<GalleriesApiResponse>(url);
  return data;
}

export async function getGalleryBySlug(slug: string): Promise<{ data: { gallery: GalleriesApiItem }; message: string | null; next: unknown; errors: unknown; meta: unknown[] }> {
  const { data } = await axiosInstance.get(`/galleries/${slug}`);
  return data;
}

export async function createGallery(payload: CreateGalleryPayload): Promise<CreateGalleryResponse> {
  const { data } = await axiosInstance.post("/galleries", payload, GALLERY_KEY_ERRORS);
  return data;
}

/**
 * Only the gallery creator may update, and the gallery must be `is_public`.
 *
 * ITEM FIELDS ARE OPTIONAL AND THE DIFFERENCE IS DESTRUCTIVE. Omit them and
 * the contents are untouched; send them and the list is SYNCED — ids not in
 * the request are removed from the gallery. `UpdateGalleryPayload` types that,
 * and the caller decides by passing the result of `buildGalleryItemsPayload`
 * only when the selection actually changed.
 */
export async function updateGallery(id: number | string, payload: UpdateGalleryPayload): Promise<CreateGalleryResponse> {
  const { data } = await axiosInstance.post(`/galleries/update/${id}`, payload, GALLERY_KEY_ERRORS);
  return data;
}

/**
 * ADD to a gallery's contents without disturbing what is already there.
 *
 * This is the non-destructive counterpart to the update sync: existing items
 * stay, ids already present are skipped rather than duplicated, and only the
 * caller's own NFTs/collections are accepted (`messages.gallery.models.not_owner`
 * otherwise). Owner only.
 *
 * Use this — not `updateGallery` — whenever the intent is "add these", because
 * the sync would remove everything the request did not mention.
 */
export async function addGalleryItems(id: number | string, payload: GalleryItemsPayload): Promise<CreateGalleryResponse> {
  const { data } = await axiosInstance.post(`/galleries/${id}/items`, payload, GALLERY_KEY_ERRORS);
  return data;
}

/**
 * REMOVE the named items from a gallery. Owner only.
 *
 * The body travels under axios's `data` option because `DELETE` carries no
 * body in axios's positional signature — `axios.delete(url, config)`, not
 * `(url, body)`. Passing the payload as the second argument would send it as
 * the config object and the request would arrive with an empty body, which the
 * backend would read as "no items named".
 */
export async function removeGalleryItems(id: number | string, payload: GalleryItemsPayload): Promise<CreateGalleryResponse> {
  const { data } = await axiosInstance.delete(`/galleries/${id}/items`, { data: payload, ...GALLERY_KEY_ERRORS });
  return data;
}

/** Body: action ∈ {follow, unfollow}. */
export async function toggleGalleryFollow(galleryId: number | string, action: "follow" | "unfollow"): Promise<any> {
  const { data } = await axiosInstance.post(`/galleries/follow/${galleryId}`, { action });
  return data;
}

/** Owner invites a user; returns the invitation record plus a deep link. */
export async function inviteToGallery(galleryId: number, userId: number): Promise<any> {
  const { data } = await axiosInstance.post(`/galleries/invite`, {
    gallery_id: galleryId,
    user_id: userId,
  });
  return data;
}

/**
 * Invitee looks up the invitation record and the next step.
 *
 * Authenticated read: the backend resolves *which* invitee is asking from the
 * bearer token `axiosInstance` attaches, so this must never be reached as a
 * plain browser navigation to the endpoint (see
 * features/notifications/gallery-invite.ts).
 *
 * `suppressToast` because the invite route renders the failure itself, and it
 * distinguishes an invalid invitation from a temporary load failure — a generic
 * toast on top of that said the opposite thing for a 5xx. A 401 still logs out
 * and opens the login modal: the interceptor handles that before the
 * suppressToast check.
 */
export async function viewGalleryInvitation(galleryId: number | string, inviteUuid: string): Promise<any> {
  const { data } = await axiosInstance.get(
    `/galleries/user-join/${galleryId}/${encodeURIComponent(inviteUuid)}`,
    { suppressToast: true } as any,
  );
  return data;
}

/**
 * Invitee accepts the invitation, becoming a member of the gallery. Separate
 * from `viewGalleryInvitation` by design — opening the invitation must not join
 * the gallery; only this explicit action does.
 *
 * `suppressToast` because the invite route already surfaces the backend's own
 * message on failure, which otherwise toasted twice.
 *
 * The endpoint requires `status` ("The status field is required." when the
 * body was empty); accepting is `accept`, the same word the join-request
 * moderation (`acceptGalleryJoin`) sends.
 */
export async function acceptGalleryInvitation(galleryId: number | string, inviteUuid: string): Promise<any> {
  const { data } = await axiosInstance.post(
    `/galleries/accept-user-join/${galleryId}/${encodeURIComponent(inviteUuid)}`,
    { status: "accept" },
    { suppressToast: true } as any,
  );
  return data;
}

/** Members of a gallery, optionally narrowed by user_id. */
export async function getGalleryMembers(galleryId: number | string, userId?: number): Promise<any> {
  const query = userId ? `?user_id=${userId}` : "";
  const { data } = await axiosInstance.get(`/galleries/list-user/${galleryId}${query}`);
  return data;
}

/** Submits a join request as the authenticated user. */
export async function requestJoinGallery(galleryId: number): Promise<any> {
  const { data } = await axiosInstance.post(`/galleries/join`, { gallery_id: galleryId });
  return data;
}

/** Gallery admin accepts (or blocks) a pending join request. */
export async function acceptGalleryJoin(galleryId: number, userId: number, status: "accept" | "block"): Promise<any> {
  const { data } = await axiosInstance.post(`/galleries/accept-join`, {
    gallery_id: galleryId,
    user_id: userId,
    status,
  });
  return data;
}
